import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Flame, Plus, Trash2, Upload, X, Loader2, ExternalLink, Smartphone, Apple,
  Send, Copy, CheckCircle2, AlertTriangle, RefreshCw,
} from "lucide-react";

function CapacityBar({ used, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const color = pct >= 100 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-green-500";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-[hsl(var(--kb-muted))]">{used} of {max} tenants assigned</span>
        <span className="font-mono text-[hsl(var(--kb-text))]">{pct}%</span>
      </div>
      <div className="h-1.5 bg-[hsl(var(--kb-surface-alt))] overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ShardRow({ shard, onDelete, onPickTenants }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="border border-[hsl(var(--kb-border))] bg-white p-5 space-y-4"
         data-testid={`shard-row-${shard.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Flame size={16} className="text-orange-600" />
            <h3 className="font-display text-lg">{shard.name}</h3>
          </div>
          <div className="text-xs text-[hsl(var(--kb-muted))] font-mono mt-1">
            Project ID: <b>{shard.project_id}</b>
          </div>
          <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">
            SA: {shard.client_email}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPickTenants(shard)}
            className="kb-btn-outline text-xs"
            data-testid={`shard-view-tenants-${shard.id}`}
          >
            View tenants
          </button>
          <button
            disabled={busy || shard.assigned_tenants_count > 0}
            onClick={async () => {
              if (!window.confirm(`Remove shard "${shard.name}"?`)) return;
              setBusy(true);
              try {
                await onDelete(shard.id);
              } finally { setBusy(false); }
            }}
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title={shard.assigned_tenants_count > 0 ? "Reassign all tenants first" : "Remove shard"}
            data-testid={`shard-delete-${shard.id}`}
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
            Remove
          </button>
        </div>
      </div>
      <CapacityBar used={shard.assigned_tenants_count} max={shard.max_apps} />
    </div>
  );
}

/** Which stores cannot receive app notifications, and why (read-only).
 *  A store pointing at an unregistered shard fails every app push. */
function StoreHealth() {
  const [state, setState] = useState({ loading: true, items: [], error: null });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/superadmin/firebase/health");
      setState({ loading: false, items: data.items || [], error: null });
    } catch (e) {
      setState({ loading: false, items: [], error: e?.response?.data?.detail || "Could not check stores" });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const broken = state.items.filter((r) => !r.ok);
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4 sm:p-5" data-testid="firebase-health">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-xs text-[hsl(var(--kb-muted))] uppercase tracking-wider">Store app-push health</div>
        <button type="button" onClick={load} className="kb-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5 min-h-[36px]" data-testid="firebase-health-refresh">
          {state.loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-check
        </button>
      </div>
      {state.error && <div className="text-sm text-red-700" role="alert">{state.error}</div>}
      {!state.loading && !state.error && (broken.length === 0 ? (
        <div className="text-sm text-green-800 flex items-center gap-1.5" data-testid="firebase-health-ok">
          <CheckCircle2 size={14} /> All {state.items.length} stores can send app notifications.
        </div>
      ) : (
        <>
          <div className="text-sm text-red-800 flex items-start gap-1.5 mb-3" data-testid="firebase-health-summary">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{broken.length} of {state.items.length} stores cannot send app notifications — every app push to them fails.</span>
          </div>
          <ul className="space-y-2">
            {broken.map((r) => (
              <li key={r.tenant_id} className="border border-red-200 bg-red-50 p-3 text-sm" data-testid={`firebase-health-row-${r.slug}`}>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <span className="font-medium break-all">{r.name || r.slug} <span className="text-[hsl(var(--kb-muted))] font-normal">({r.slug})</span></span>
                  <span className="text-xs text-[hsl(var(--kb-muted))]">{r.device_tokens} app device{r.device_tokens === 1 ? "" : "s"} · {r.mode} mode</span>
                </div>
                <div className="text-xs text-red-800 mt-1 break-words [overflow-wrap:anywhere]" data-testid={`firebase-health-problem-${r.slug}`}>{r.problem}</div>
              </li>
            ))}
          </ul>
        </>
      ))}
    </div>
  );
}

export default function SuperAdminFirebase() {
  const [shards, setShards] = useState([]);
  const [maxPer, setMaxPer] = useState(15);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [shardName, setShardName] = useState("");
  const [saText, setSaText] = useState("");
  const [busy, setBusy] = useState(false);
  const [tenantsModal, setTenantsModal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/superadmin/firebase/shards");
        if (cancelled) return;
        setShards(data.shards || []);
        setMaxPer(data.max_tenants_per_shard || 15);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load shards");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/superadmin/firebase/shards");
      setShards(data.shards || []);
      setMaxPer(data.max_tenants_per_shard || 15);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load shards");
    }
  };

  const addShard = useCallback(async () => {
    const trimmedName = shardName.trim();
    const trimmedSa = saText.trim();
    if (!trimmedName) { toast.error("Shard name required"); return; }
    if (!trimmedSa) { toast.error("Paste the service-account JSON"); return; }
    try {
      JSON.parse(trimmedSa);
    } catch {
      toast.error("Invalid JSON");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("name", trimmedName);
      form.append("service_account_json", trimmedSa);
      await api.post("/superadmin/firebase/shards", form);
      toast.success("Shard added");
      setShowAdd(false);
      setShardName(""); setSaText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add shard");
    } finally { setBusy(false); }
  }, [shardName, saText]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setSaText(text);
    // Auto-fill name from project_id if blank
    if (!shardName) {
      try {
        const j = JSON.parse(text);
        if (j.project_id) setShardName(`Shard — ${j.project_id}`);
      } catch { /* ignore */ }
    }
  };

  const deleteShard = async (shardId) => {
    try {
      await api.delete(`/superadmin/firebase/shards/${shardId}`);
      toast.success("Shard removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const pickTenants = async (shard) => {
    try {
      const { data } = await api.get(`/superadmin/firebase/shards/${shard.id}/tenants`);
      setTenantsModal({ shard, items: data.items || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tenants");
    }
  };

  const totalSlots = shards.reduce((s, sh) => s + (sh.max_apps || 0), 0);
  const totalUsed = shards.reduce((s, sh) => s + (sh.assigned_tenants_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="super-firebase-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl flex items-center gap-2">
            <Flame className="text-orange-600" /> Firebase shards
          </h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1 max-w-2xl">
            Firebase caps every project at 30 apps. With 1 Android + 1 iOS per tenant
            that&apos;s <b>{maxPer} tenants per shard</b>. Add a new shard when capacity
            tightens — tenants are auto-assigned to the shard with the most room.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="kb-btn-primary text-sm inline-flex items-center gap-1.5"
          data-testid="add-shard-btn"
        >
          <Plus size={14} /> Add shard
        </button>
      </div>

      <StoreHealth />

      {/* Capacity summary */}
      {shards.length > 0 && (
        <div className="bg-white border border-[hsl(var(--kb-border))] p-5">
          <div className="text-xs text-[hsl(var(--kb-muted))] uppercase tracking-wider mb-2">
            Platform capacity
          </div>
          <div className="font-display text-2xl mb-3">
            {totalUsed} / {totalSlots} tenants
            <span className="text-sm text-[hsl(var(--kb-muted))] ml-2">across {shards.length} shard{shards.length === 1 ? "" : "s"}</span>
          </div>
          <CapacityBar used={totalUsed} max={totalSlots} />
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />)}
        </div>
      ) : shards.length === 0 ? (
        <div className="bg-white border border-dashed border-[hsl(var(--kb-border))] p-12 text-center"
             data-testid="shards-empty">
          <Flame size={36} className="mx-auto text-orange-400 mb-3" />
          <h2 className="font-display text-2xl mb-2">No Firebase shards yet</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] max-w-md mx-auto mb-6">
            Add your first Firebase project so we can auto-provision Android + iOS apps
            for every tenant. The service-account JSON needs <b>Firebase Admin</b> IAM role.
          </p>
          <button onClick={() => setShowAdd(true)} className="kb-btn-primary inline-flex items-center gap-2" data-testid="empty-add-shard-btn">
            <Plus size={14} /> Add first shard
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {shards.map((sh) => (
            <ShardRow key={sh.id} shard={sh} onDelete={deleteShard} onPickTenants={pickTenants} />
          ))}
        </div>
      )}

      {/* Add Shard Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
             onClick={() => !busy && setShowAdd(false)}>
          <div className="bg-white max-w-xl w-full p-6 space-y-4"
               onClick={(e) => e.stopPropagation()}
               data-testid="add-shard-dialog">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Add Firebase shard</h2>
              <button onClick={() => setShowAdd(false)} className="kb-btn-outline text-sm" disabled={busy}>
                <X size={14} />
              </button>
            </div>
            <p className="text-xs text-[hsl(var(--kb-muted))]">
              Create a new Firebase project at <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer" className="underline">console.firebase.google.com</a>,
              then generate a service-account key (Project Settings → Service Accounts → Generate new private key).
              The SA needs the <b>Firebase Admin</b> role in Google Cloud IAM.
            </p>
            <label className="text-xs flex flex-col">
              <span>Shard name (display only)</span>
              <input className="kb-input"
                     value={shardName}
                     onChange={(e) => setShardName(e.target.value)}
                     placeholder="e.g. Production Shard 1"
                     data-testid="shard-name-input" />
            </label>
            <label className="text-xs flex flex-col">
              <span>Upload service-account JSON</span>
              <input type="file" accept="application/json,.json"
                     onChange={onFileChange}
                     className="kb-input pt-2"
                     data-testid="shard-file-input" />
            </label>
            <div className="text-xs text-[hsl(var(--kb-muted))] text-center">— or paste JSON —</div>
            <label className="text-xs flex flex-col">
              <textarea
                rows={6}
                className="kb-input font-mono text-xs"
                placeholder='{ "type": "service_account", "project_id": "...", "private_key": "...", ... }'
                value={saText}
                onChange={(e) => setSaText(e.target.value)}
                data-testid="shard-sa-textarea"
              />
            </label>
            <button onClick={addShard} disabled={busy} className="kb-btn-primary w-full text-sm inline-flex items-center justify-center gap-2"
                    data-testid="shard-submit-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {busy ? "Adding…" : "Add shard"}
            </button>
          </div>
        </div>
      )}

      {/* Per-shard tenants modal */}
      {tenantsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
             onClick={() => setTenantsModal(null)}>
          <div className="bg-white max-w-3xl w-full p-6 space-y-4"
               onClick={(e) => e.stopPropagation()}
               data-testid="shard-tenants-dialog">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl">{tenantsModal.shard.name}</h2>
                <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">
                  Project ID: {tenantsModal.shard.project_id}
                </div>
              </div>
              <button onClick={() => setTenantsModal(null)} className="kb-btn-outline text-sm"><X size={14} /></button>
            </div>
            {tenantsModal.items.length === 0 ? (
              <div className="text-sm text-[hsl(var(--kb-muted))] py-8 text-center">No tenants assigned yet.</div>
            ) : (
              <div className="space-y-2">
                {tenantsModal.items.map((t) => (
                  <div key={t.id} className="border border-[hsl(var(--kb-border))] p-3 flex items-center justify-between flex-wrap gap-2"
                       data-testid={`shard-tenant-${t.slug}`}>
                    <div className="text-sm">
                      <div className="font-medium">{t.name || t.slug}</div>
                      <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">
                        <span className="inline-flex items-center gap-1">
                          <Smartphone size={10} /> {t.android_package_name || "—"}
                        </span>
                        <span className="mx-2">·</span>
                        <span className="inline-flex items-center gap-1">
                          <Apple size={10} /> {t.ios_bundle_id || "—"}
                        </span>
                      </div>
                    </div>
                    {t.firebase_provisioned_at ? (
                      <span className="text-xs text-green-700 inline-flex items-center gap-1">
                        <CheckCircle2 size={11} /> provisioned
                      </span>
                    ) : (
                      <span className="text-xs text-amber-700">awaiting provision</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
